import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { shellActions } from './shellActions.js';

// The one theme brain: what the person chose, and what the document shows.
//
// It replaces a service in the phone app and a composable in the desktop app
// that had drifted apart — different names for the same three choices, and
// different places to save them. What is shared lives here. What differs by
// platform — where a choice is saved — is in the shell's actions.

const SYSTEM = 'system';
const LIGHT = 'light';
const DARK = 'dark';
const THEMES = [SYSTEM, LIGHT, DARK];

// One class on <html> is the whole switch. PrimeVue is configured against it in
// both apps, and so is the component library.
const DARK_CLASS = 'p-dark';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemQuery() {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return null;
    }
    return window.matchMedia(DARK_QUERY);
}

const useThemeStore = defineStore('shellTheme', function () {
    const actions = shellActions().theme;

    // What the person chose. `system` follows the device.
    const theme = ref(SYSTEM);
    const systemIsDark = ref(systemQuery()?.matches === true);

    const isDark = computed(function () {
        if (theme.value === SYSTEM) {
            return systemIsDark.value;
        }
        return theme.value === DARK;
    });

    function isTheme(value) {
        return THEMES.includes(value);
    }

    // Show a choice. Anything unrecognized — a value saved by a future version —
    // follows the device.
    function apply(next) {
        if (isTheme(next)) {
            theme.value = next;
        } else {
            theme.value = SYSTEM;
        }
        actions.rememberFirstFrame(theme.value);
        if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle(DARK_CLASS, isDark.value);
        }
    }

    // What a person choosing a theme does. Show first, save second: the visible
    // switch is immediate and the write may not be, so the screen never sits
    // half-applied while storage settles. An unknown value is ignored rather
    // than thrown — the caller is a control, where an exception is a broken
    // screen.
    async function choose(next) {
        if (!isTheme(next)) {
            return;
        }
        apply(next);
        await actions.save(next);
    }

    // Start from what is known before the first frame, then from what was
    // saved. On a desktop the two are the same read.
    apply(actions.firstFrame());
    const ready = actions.load().then(function (saved) {
        if (isTheme(saved)) {
            apply(saved);
        }
    });

    // Follow the device for as long as the app is open. A device that crosses
    // into dark at sunset moves a person on `system` and leaves an explicit
    // choice alone.
    systemQuery()?.addEventListener('change', function (event) {
        systemIsDark.value = event.matches;
        apply(theme.value);
    });

    return { theme, isDark, ready, choose };
});

export { THEMES, useThemeStore };
